import { useState } from 'react';
import { Alert, Button, Checkbox, FileButton, Group, Text, UnstyledButton } from '@mantine/core';
import { IconPhotoOff, IconPhotoPlus, IconPencil } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export type QuotePhoto = { id: string; url: string; label: string; available: boolean; productId?: string; dataUrl?: string };
export async function prepareQuotePhotos(files: File[]): Promise<QuotePhoto[]> {
  const result: QuotePhoto[] = [];
  for (const file of files) {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1,1400/Math.max(bitmap.width,bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width=Math.max(1,Math.round(bitmap.width*scale)); canvas.height=Math.max(1,Math.round(bitmap.height*scale));
    const context=canvas.getContext('2d'); if(!context) throw new Error('Image unavailable');
    context.fillStyle='#fff';context.fillRect(0,0,canvas.width,canvas.height);
    context.drawImage(bitmap,0,0,canvas.width,canvas.height); bitmap.close();
    const dataUrl=canvas.toDataURL('image/jpeg',.9);
    if(dataUrl.length>4000000) throw new Error('Image too large');
    result.push({id:`upload-${crypto.randomUUID()}`,url:dataUrl,dataUrl,label:file.name,available:true});
  }
  return result;
}
type Props={photos:QuotePhoto[]; selected:string[]; onChange:(ids:string[])=>void; onAdd:(photos:QuotePhoto[])=>void; onReplace:(id:string,photo:QuotePhoto)=>void; limit:number};
export function QuoteImageGallery({photos,selected,onChange,onAdd,onReplace,limit}:Props) {
  const {t}=useTranslation();const label=(key:string)=>t(`opportunity.quote.${key}`);
  const [busy,setBusy]=useState(false);const [error,setError]=useState(false);
  const available=photos.filter(photo=>photo.available);
  const missing=photos.length-available.length;
  return <div>
    <Group position="apart" mb="xs">
      <Text size="sm" weight={600}>{label('imageGallery')} ({selected.length}/{limit})</Text>
      <FileButton multiple accept="image/png,image/jpeg,image/webp" onChange={async files=>{
        setBusy(true);setError(false);
        try { const added=await prepareQuotePhotos(files);onAdd(added);onChange([...selected,...added.map(photo=>photo.id)].slice(0,limit)); }
        catch {setError(true);} finally {setBusy(false);}
      }}>{props=><Button {...props} variant="light" size="xs" loading={busy} leftIcon={<IconPhotoPlus size={15}/>}>{label('addImages')}</Button>}</FileButton>
    </Group>
    {error&&<Alert color="red" mb="xs">{label('imageError')}</Alert>}
    {!available.length&&<Text size="sm" color="dimmed">{label('noImages')}</Text>}
    {!!missing&&<Text size="xs" color="dimmed" mb="xs">{t('opportunity.quote.missingImageCount',{count:missing})}</Text>}
    <Text size="xs" color="dimmed" mb="xs">{label('clickImageToReplace')}</Text>
    <div className="quote-photo-gallery">{photos.map(photo=>{
      const checked=selected.includes(photo.id);
      return <div key={photo.id} className={`quote-photo ${checked?'quote-photo-selected':''}`}>
        <FileButton accept="image/png,image/jpeg,image/webp" onChange={async file=>{
          if(!file)return;setBusy(true);setError(false);
          try {
            const [replacement]=await prepareQuotePhotos([file]);
            const updated={...replacement,productId:photo.productId};
            onReplace(photo.id,updated);
            onChange(checked?selected.map(id=>id===photo.id?updated.id:id):[...selected,updated.id].slice(0,limit));
          } catch {setError(true);} finally {setBusy(false);}
        }}>{props=><UnstyledButton {...props} className="quote-photo-replace" disabled={busy} aria-label={`${label('replaceImage')}: ${photo.label}`}>
          {photo.available?<img src={photo.url} alt={photo.label}/>:<span className="quote-photo-missing"><IconPhotoOff size={32}/><Text size="xs">{label('replaceImage')}</Text></span>}
          <span className="quote-photo-edit"><IconPencil size={14}/></span>
        </UnstyledButton>}</FileButton>
        <Text size="xs" lineClamp={2} mt={4} title={photo.label}>{photo.label}</Text>
        <Checkbox mt={6} size="xs" label={label('useImage')} checked={checked} disabled={!photo.available||(!checked&&selected.length>=limit)}
          onChange={()=>onChange(checked?selected.filter(id=>id!==photo.id):[...selected,photo.id])}/>
      </div>;
    })}</div>
    {!!selected.length&&<Group spacing={6} mt="xs">{selected.map((id,index)=><Button key={id} size="xs" variant="subtle" disabled={index===0} title={label('moveImageFirst')}
      onClick={()=>onChange([id,...selected.filter(value=>value!==id)])}>{index+1}. {photos.find(photo=>photo.id===id)?.label.slice(0,22)}</Button>)}</Group>}
  </div>;
}
